import { useContext, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { CordeliaLogoSvgPPP } from "./components/svgImages";
import { getPublicQuotationDetails, getUpdatedQuotationPrice } from "../../api/quotation.api";
import { createPaymentLink } from "../../api/payment.api";
import { Spinner } from "react-bootstrap";
import Checkout from "nimbbl_sonic";
import AccommodationDetailsCard from "./components/AccommodationDetailsCard";
import PricePaymentCard from "./components/PricePaymentCard";
import TripDetailsCard from "./components/TripDetailsCard";
import TaxInfoCard from "./components/TaxInfoCard";
import BillingDetailsCard from "./components/BillingDetailsCard";
import ExpiryCountdown from "./components/ExpiryCountdown";
import TermsConditions from "./components/TermsConditions";
import GstDetailsCard from "./components/GstDetailsCard";
import CouponCodeCard from "./components/CouponCodeCard";
import NotFound from "../404/NotFound";
// import analytics from "../../utils/googleAnalytics";
import PriceUpdateModal from "./components/PublicModalWrapper/PriceUpdateModal";
import PublicModalWrapper from "./components/PublicModalWrapper";
import CabinBlockModal from "./components/PublicModalWrapper/CabinBlockedModal";
import SnackbarContext from "../../context/snackbar.context";
import FullPageLoader from "../../components/FullPageLoader/FullPageLoader";
import "./style.scss";
export interface IPaymentPayload {
  quotationId: string;
  paymentType: string;
  amount: number;
  paymentToken: string;
  address: string;
  email: string;
  firstName: string;
  isNri: boolean;
  pan?: string;
  postalCode: string;
  phoneNumber: string;
  tcsSlab?: number;
  taxRegime?: string;
  gstNumber?: string;
  gstName?: string;
};

export interface IData {
  itineraryDetails: any;
  portSequence: any[];
  quotationDetails: any;
  bookingDetails: any;
  quotationCoupon: any;
  partialOptions: any;
  splitPaymentOptions: any;
  cancellationPolicies: { terms: string[] }[];
  cancellationTableData: string[];
  reschedulePolicies: { terms: string[] }[];
  rescheduleTableData: string[];
  resinstatePolicies: string[];
};

export interface IPriceData {
  cabin_fares: number[];
  cabin_names: string[];
  discount_category: any;
  discount: number;
  port_charges: number;
  fuel_surcharge: number;
  gratuity: number;
  gst: number;
  service_tax_and_levies: number;
  ssr_fares: number[];
  ssr_names: string[];
  total: number;
};

const formatDate = (isoDate: string) => {
  const date = new Date(isoDate);
  const formattedDate = date.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
  const formattedTime = date.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: true });
  return `${formattedDate} | ${formattedTime}`;
};

const fieldsDisabled = (data: IData) => {
  return (
    data?.bookingDetails?.status === "CONFIRMED" ||
    data?.bookingDetails?.status === "CANCELLED" ||
    data?.bookingDetails?.status === "RESERVED" ||
    data?.quotationDetails?.status === "PAYMENT_PENDING" ||
    data?.quotationDetails?.status === "EXPIRED" ||
    data?.quotationDetails?.status === "REJECTED" ||
    (data?.itineraryDetails?.status === "BOOKED" && !data?.bookingDetails)
  );
};

const handleExpiryDate = (data: IData) => {
  if (data?.quotationDetails?.status === "EXPIRED" || data?.itineraryDetails?.status !== "BOOKING")
    return "1980-01-01T00:00:00Z";
  else
    return data?.bookingDetails?.createdAt || data?.quotationDetails?.sharedAt || data?.quotationDetails?.createdAt;
};

const PublicPaymentPage = () => {
  const navigate = useNavigate();
  const params = useParams();
  const { quotationId = "" } = params;
  const snackbarCtx = useContext(SnackbarContext);

  const [loader, setLoader] = useState<boolean>(true);
  const [paymentLoader, setPaymentLoader] = useState<boolean>(false);

  const [error404, setError404] = useState<boolean>(false);
  const [timerExpired, setTimerExpired] = useState<boolean>(true);
  const [data, setData] = useState<IData>({ itineraryDetails: {}, portSequence: [], quotationDetails: {}, bookingDetails: {}, quotationCoupon: {},
    partialOptions: {}, splitPaymentOptions: {}, cancellationPolicies: [], cancellationTableData: [], reschedulePolicies: [], rescheduleTableData: [], resinstatePolicies: [] });
  const [internationalType, setInternationalType] = useState<boolean>(false); // true: INTERNATIONAL && false: DOMESTIC
  const [editAccess, setEditAccess] = useState<boolean>(false);
  const [paymentGoAhead, setPaymentGoAhead] = useState<boolean>(false);
  const [paymentPayload, setPaymentPayload] = useState<IPaymentPayload>({ quotationId: quotationId, paymentType: "", amount: 0, 
    paymentToken: "", address: "", email: "", firstName: "", isNri: false, postalCode: "", phoneNumber: "" });
  const [gstErrorsObj, setGstErrorsObj] = useState({ gstNumber: false, gstName: false });  
  const [price, setPrice] = useState<IPriceData>({ total: 0, cabin_names: [], cabin_fares: [], ssr_names: [], ssr_fares: [], service_tax_and_levies: 0, gst: 0, discount_category: {}, discount: 0, port_charges: 0, gratuity: 0, fuel_surcharge: 0 });
  const [updatedPrice, setUpdatedPrice] = useState<IPriceData>({ total: 0, cabin_names: [], cabin_fares: [], ssr_names: [], ssr_fares: [], service_tax_and_levies: 0, gst: 0, discount_category: {}, discount: 0, port_charges: 0, gratuity: 0, fuel_surcharge: 0 });
  const [priceUpdateModalState, setPriceUpdateModalState] = useState<boolean>(false);
  const [cabinBlockedModalState, setCabinBlockedModalState] = useState<boolean>(false);

  useEffect(() => { getQuotationData();}, []);

  useEffect(() => {
    if (!loader) {
      if (!data?.bookingDetails && !data?.quotationDetails?.contact) {
        setEditAccess(true);
        if (internationalType)
          setPaymentPayload({
            ...paymentPayload,
            tcsSlab: 20,
            taxRegime: "NEW",
          });
      } else {
        setEditAccess(false);
        setPaymentGoAhead(true);
        if (!!data?.quotationDetails?.contact) {
          const { phone, address1, postCode, international, id, bookingId, address2, city, state, country, createdAt, updatedAt, deletedAt,
            countryCode, phoneOtp, phoneOtpSentAt, phoneOtpVerifiedAt, gstin, panName, lastName, gstName, ...tempObj } = data.quotationDetails.contact;
          if (phone) tempObj.phoneNumber = phone;
          if (address1) tempObj.address = address1;
          if (postCode) tempObj.postalCode = postCode;
          if (international) tempObj.isNri = international;

          setPaymentPayload((prevPaymentPayload) => ({
            ...prevPaymentPayload,
            ...tempObj,
          }));
        }

        if (!!data?.quotationDetails?.quotationTcs.length) {
          const { id, quotationId, bookingId, createdAt, updatedAt, deletedAt, ...tempObj } = data.quotationDetails.quotationTcs[0];
          const filteredTempObj = Object.fromEntries(
            Object.entries(tempObj).filter(([_, value]) => value !== null)
          );
          setPaymentPayload((prevPaymentPayload) => ({
            ...prevPaymentPayload,
            ...filteredTempObj,
          }));
        }
      }

      if (!fieldsDisabled(data)) getUpdatedPrices(false);  

      // analytics.track('add_to_cart', {
      //   currency: "INR",
      //   value: price?.total,
      //   name: data?.quotationDetails?.routeName,
      //   items: data?.quotationDetails?.items?.map((item: any) => ({
      //     item_variant: 'cabin',
      //     item_list_name: item?.categoryName,
      //     item_category3: item?.roomCategory?.code,
      //     item_id: data?.quotationDetails?.itineraryId,
      //     item_name: data?.quotationDetails?.routeName,
      //     item_category2: format(parseISO(data?.quotationDetails?.itinerary?.startTime), 'dd/MM/yyyy'),
      //     item_category: `${data?.quotationDetails?.itinerary?.nightCount} nights`,
      //     price: item?.cabinFare,
      //   })),
      // });
    }
  }, [data]);

  useEffect(() => {
    if (!!updatedPrice.total && (Math.abs(Number(price.total.toFixed(2)) - Number(updatedPrice.total.toFixed(2))) > 1)) { setPriceUpdateModalState(true); setPaymentLoader(false) }
    else if (!!updatedPrice.total && !!paymentPayload.paymentType) triggerCreatePaymentLink();
  }, [updatedPrice]);

  useEffect(() => {
    if(!!paymentPayload.paymentToken) handleNimbleCheckout(paymentPayload);
  }, [paymentPayload.paymentToken]);
  
  const getQuotationData = async () => {
    if (quotationId) {
      const data = await getPublicQuotationDetails(quotationId);
      if (data?.status === 200) {
        const { result } = data;
        setData({
          itineraryDetails: result?.itinerary?.details,
          portSequence: result?.itinerary?.port_seq,
          quotationDetails: result?.quotation,
          bookingDetails: result?.booking,
          quotationCoupon: result?.quotationCoupon,
          partialOptions: result?.partialOptions,
          splitPaymentOptions: result?.splitPayment,
          cancellationPolicies: result?.cancellationPolicies,
          cancellationTableData: result?.cancellationItems,
          reschedulePolicies: result?.reschedulePolicies,
          rescheduleTableData: result?.rescheduleItems,
          resinstatePolicies: result?.resinstatePolicies
        });
        result?.itinerary?.details?.type === "INTERNATIONAL"
          ? setInternationalType(true)
          : setInternationalType(false);
        setPaymentPayload({
          ...paymentPayload,
          quotationId: result?.quotation?.id,
        });
        calculatePrice(result?.pricing, false);
        setLoader(false);
      } else if (data?.response?.data?.customErrorNumber === 110001) {
        setLoader(false);
        setError404(true);
      } else console.error(data?.response?.data?.message);
    }
  };

  const getUpdatedPrices = async (wantLoader: boolean) => {
    setPaymentLoader(wantLoader);
    const updatedPriceData = await getUpdatedQuotationPrice(quotationId);
    if (updatedPriceData?.status === 200) calculatePrice(updatedPriceData?.result, true);
    else if (updatedPriceData?.response?.data?.customErrorNumber === 9999) { setCabinBlockedModalState(true); setPaymentLoader(false) }
    else {
      snackbarCtx.toggleSnackbar({
        message: updatedPriceData?.response?.data?.message || "Couldn't fetch latest updated prices !!",
        type: "FAILURE",
        visible: true,
      });
      setPaymentLoader(false);
    }
  }

  const triggerCreatePaymentLink = async () => {
    setPaymentLoader(true);
    const data = await createPaymentLink(paymentPayload);
    if (data?.status === 201) {
      setPaymentPayload({
        ...paymentPayload,
        paymentToken: data?.result?.data?.token,
      });
    } else {
      const errMessage = data?.response?.data?.message;
      snackbarCtx.toggleSnackbar({
        message: errMessage?.[0]?.toUpperCase() + errMessage?.slice(1),
        type: "FAILURE",
        visible: true,
      });
    }
  }

  // const getUpdatedPrices = async (wantLoader: boolean) => {
  //   setPaymentLoader(wantLoader);
  //   const updatedPriceData = await getUpdatedQuotationPrice(quotationId);
  //   if (updatedPriceData?.status === 200) calculatePrice(updatedPriceData?.result, true);
  //   else if (updatedPriceData?.response?.data?.customErrorNumber === 9999) { setCabinBlockedModalState(true); setPaymentLoader(false) };
  // }

  const encodeBase64Param = (responseObj: any) => {
    try {
      const jsonString = JSON.stringify(responseObj);
      const encodedString = btoa(jsonString);
      return encodedString;
    } catch (error) {
      snackbarCtx.toggleSnackbar({
        message: 'Failed to encode response object !!',
        type: "FAILURE",
        visible: true,
      });
    }
  };
  
  const handleNimbleCheckout = (payload: any) => {
    const res = new Checkout({
      token: payload?.paymentToken,
    });
  
    res.open({
      callback_handler: function (response: any) {
        Promise.resolve()
          .then(() => {
            try {
              if (!response?.payload?.is_callback) {
                snackbarCtx.toggleSnackbar({
                  message: 'Invalid nimble callback response',
                  type: "FAILURE",
                  visible: true,
                });
              }
              const responseEncoded = encodeBase64Param({
                quotationId: quotationId,
                ...response?.payload,
              });
              return responseEncoded;
            } catch (error) {
              snackbarCtx.toggleSnackbar({
                message: 'Encoding error of nimble callback response',
                type: "FAILURE",
                visible: true,
              });
            }
          })
          .then((responseEncoded) => {
            try {
              // Navigate to the payment status page with the encoded response
              navigate(`/payment-status/${responseEncoded}`);
            } catch (navigationError) {
              snackbarCtx.toggleSnackbar({
                message: 'Navigation failed to payment status page!!',
                type: "FAILURE",
                visible: true,
              });
            }
          })
          .catch((error) => {
            snackbarCtx.toggleSnackbar({
              message: error || 'Something went wrong !!',
              type: "FAILURE",
              visible: true,
            });
          });
      },
      redirect: false,
    });
  };

  // const triggerGAEvent = () => {
  //   console.log('trigger GA event');
  //   analytics.track('begin_checkout', {
  //     currency: "INR",
  //     value: paymentPayload?.amount,
  //     name: data?.quotationDetails?.routeName,
  //     items: data?.quotationDetails?.items?.map((item: any) => ({
  //       item_variant: 'cabin',
  //       item_list_name: item?.categoryName,
  //       item_category3: item?.roomCategory?.code,
  //       item_id: data?.quotationDetails?.itineraryId,
  //       item_name: data?.quotationDetails?.routeName,
  //       item_category2: format(parseISO(data?.quotationDetails?.itinerary?.startTime), 'dd/MM/yyyy'),
  //       item_category: `${data?.quotationDetails?.itinerary?.nightCount} nights`,
  //       price: item?.cabinFare,
  //     })),
  //   });
  // }

  const calculatePrice = (priceObj: any[], updatedPrice: boolean) => {
    let tempObj: IPriceData = { total: 0, cabin_names: [], cabin_fares: [], ssr_names: [], ssr_fares: [], service_tax_and_levies: 0, gst: 0, discount_category: {}, discount: 0, port_charges: 0, gratuity: 0, fuel_surcharge: 0 };
    priceObj?.forEach((value: any) => {
      const discountCategoryObject = value?.discount_category?.reduce((acc: any, discountName: string) => {
        acc[discountName] = true;
        return acc;
      }, {});
      if (value.type === "CABIN") {
        tempObj = {
          ...tempObj,
          total: tempObj.total + value.cabin_fare + value.service_tax_and_levies + value.gst + value.fuel_surcharge,
          cabin_names: [...tempObj.cabin_names, value.title],
          cabin_fares: [...tempObj.cabin_fares, value.cabin_fare],
          service_tax_and_levies: tempObj.service_tax_and_levies + value.service_tax_and_levies,
          gst: tempObj.gst + value.gst,
          discount_category: { ...tempObj.discount_category, ...discountCategoryObject },
          discount: tempObj.discount + value.discount,
          port_charges: tempObj.port_charges + value.port_charges,
          fuel_surcharge: tempObj.fuel_surcharge + value.fuel_surcharge,
          gratuity: tempObj.gratuity + value.gratuity
        };
      } else {
          tempObj = {
            ...tempObj,
            total: tempObj.total + value.total + value.service_tax_and_levies + value.gst + value.fuel_surcharge,
            ssr_names: [...tempObj.ssr_names, value.title],
            ssr_fares: [...tempObj.ssr_fares, value.total],
            service_tax_and_levies: tempObj.service_tax_and_levies + value.service_tax_and_levies,
            gst: tempObj.gst + value.gst,
            discount_category: { ...tempObj.discount_category, ...discountCategoryObject },
            discount: tempObj.discount + value.discount,
            port_charges: tempObj.port_charges + value.port_charges,
            fuel_surcharge: tempObj.fuel_surcharge + value.fuel_surcharge,
            gratuity: tempObj.gratuity + value.gratuity
          };
        }
    });
    updatedPrice ? setUpdatedPrice({ ... tempObj }) : setPrice({ ...tempObj });
  };

  const handleNRICheckbox = () => {
    const tempObj: IPaymentPayload = { ...paymentPayload };
    if (!tempObj?.isNri) {
      delete tempObj.taxRegime; delete tempObj.gstName; delete tempObj.gstNumber; delete tempObj.pan;
      setPaymentPayload({ ...tempObj, tcsSlab: 20, isNri: !tempObj?.isNri });
    } else {
      setPaymentPayload({ ...tempObj, taxRegime: 'NEW', tcsSlab: 20, isNri: !tempObj?.isNri });
    }
    setPaymentGoAhead(false);
  };

  return (
    <div className="ppp-wrapper">
      {loader ? (
        <FullPageLoader />
      ) : error404 ? (
        <NotFound />
      ) : (
        <>
          <div className="head-wrapper">
            <CordeliaLogoSvgPPP />
            {data?.bookingDetails?.number ? (
              <div>
                <span>BOOKING NO: {data?.bookingDetails?.number}</span>
                <span>Creation Date: {formatDate(data?.bookingDetails?.createdAt)}</span>
              </div>
            ) : (
              <div>
                <span>QUOTATION NO: {data?.quotationDetails?.number}</span>
                <span>Creation Date: {formatDate(data?.quotationDetails?.createdAt)}</span>
              </div>
            )}
          </div>
          <div className="body-wrapper">
            <div className="left-body-wrapper">
              {data?.quotationDetails?.status === "PAYMENT_PENDING" && !(data?.bookingDetails) && <div className="status-body-wrapper">Payment for this quotation is pending. <br /> Confirmation may take around 15 minutes.</div>}
              {data?.bookingDetails?.status === "CANCELLED" && <div className="status-body-wrapper"><span className="font-bold">CANCELLED BOOKING</span></div>}
              {data?.bookingDetails?.status !== "CONFIRMED" &&
               data?.bookingDetails?.status !== "CANCELLED" &&
               data?.bookingDetails?.status !== "RESERVED" &&
               data?.quotationDetails?.status !== "REJECTED" && (
              <ExpiryCountdown date={handleExpiryDate(data)} timerExpired={timerExpired} setTimerExpired={setTimerExpired} setData={setData} /> )}
              <TripDetailsCard itineraryDetails={data?.itineraryDetails} portSequence={data?.portSequence} roomsData={data?.quotationDetails?.items} />
              <AccommodationDetailsCard roomsData={data?.quotationDetails?.items} shipName={data?.itineraryDetails?.ship_name} />
              {internationalType && (
                <div className="nri-checkbox-wrapper">
                  <label className="nri-checkbox" onChange={handleNRICheckbox}>
                    <input type="checkbox" disabled={fieldsDisabled(data)} checked={paymentPayload?.isNri} />
                    <span className="tick-mark"></span>
                    <span className={`label-text ${paymentPayload?.isNri ? "active" : ""}`}>I'm an NRI</span>
                  </label>
                  <p className={`agree-tcs-text ${paymentPayload?.isNri ? "active" : ""}`}>
                    {" "}
                    I agree to pay{" "}
                    <b>
                      <span>20% TCS (Tax Collected at Source)</span>
                    </b>{" "}
                    that is applicable on the <b>Grand Total.</b>
                  </p>
                </div>
              )}
              {internationalType && !paymentPayload?.isNri && (
                <TaxInfoCard
                  editAccess={editAccess}
                  fieldsDisabled={fieldsDisabled(data)}
                  paymentPayload={paymentPayload}
                  setPaymentPayload={setPaymentPayload}
                  paymentGoAhead={paymentGoAhead}
                  setPaymentGoAhead={setPaymentGoAhead}
                  gstErrorsObj={gstErrorsObj}
                  setGstErrorsObj={setGstErrorsObj}
                  totalAmount={price.total}
                />
              )}
              {!internationalType && !paymentPayload?.isNri && (
                <GstDetailsCard
                  fieldsDisabled={fieldsDisabled(data)}
                  paymentPayload={paymentPayload}
                  setPaymentPayload={setPaymentPayload}
                  paymentGoAhead={paymentGoAhead}
                  setPaymentGoAhead={setPaymentGoAhead}
                  gstErrorsObj={gstErrorsObj}
                  setGstErrorsObj={setGstErrorsObj}
                />
              )}
              <BillingDetailsCard
                internationalType={internationalType}
                fieldsDisabled={fieldsDisabled(data)}
                paymentPayload={paymentPayload}
                setPaymentPayload={setPaymentPayload}
                paymentGoAhead={paymentGoAhead}
                setPaymentGoAhead={setPaymentGoAhead}
                gstError={gstErrorsObj.gstName || gstErrorsObj.gstNumber}
                setGstErrorsObj={setGstErrorsObj}
              />
            </div>
            <div className="right-body-wrapper">
              {!fieldsDisabled(data) && (
              <CouponCodeCard getQuotationData={getQuotationData} couponDetails={data?.quotationCoupon} partialPayment={data?.partialOptions?.partial_allowed} />)}
              <PricePaymentCard
                priceData={price}
                discountDescription={data?.quotationDetails?.offerDescriptions}
                partialOptions={data?.partialOptions}
                splitPaymentOptions={data?.splitPaymentOptions}
                quotationActive={(data?.quotationDetails?.status === "ACTIVE" || data?.bookingDetails?.status === "RESERVED")}
                bookingData={data?.bookingDetails}
                editAccess={editAccess}
                paymentPayload={paymentPayload}
                setPaymentPayload={setPaymentPayload}
                paymentGoAhead={paymentGoAhead}
                international={internationalType}
              />
              {!(data?.bookingDetails?.status === "CONFIRMED" ||
               data?.bookingDetails?.status === "CANCELLED" ||
               data?.quotationDetails?.status === "PAYMENT_PENDING" ||
               data?.quotationDetails?.status === "EXPIRED" ||
               data?.quotationDetails?.status === "REJECTED" ||
               (data?.itineraryDetails?.status === "BOOKED" && !data?.bookingDetails)
              ) && (
                <button
                  className={`payment-button ${
                    (!!paymentPayload?.paymentType && !!paymentPayload?.amount && paymentGoAhead && !paymentLoader) ? "enabled" : "disabled"
                  }`}
                  disabled={!(!!paymentPayload?.paymentType && !!paymentPayload?.amount && paymentGoAhead && !paymentLoader)}
                  onClick={() => data?.bookingDetails?.status === "RESERVED" ? triggerCreatePaymentLink() : getUpdatedPrices(true)}
                >
                  Proceed to Payment
                  {paymentLoader && <Spinner size="sm"/>}
                </button>
              )}
            </div>
          </div>
          <TermsConditions
            cancellationTableData={data?.cancellationTableData}
            cancellationPolicies={data?.cancellationPolicies}
            rescheduleTableData={data?.rescheduleTableData}
            reschedulePolicies={data?.reschedulePolicies}
            reinstatePolicies={data?.resinstatePolicies}
            splitPolicies={(!data?.bookingDetails?.amountPaid && !!data?.splitPaymentOptions?.split_allowed) || !!data?.bookingDetails?.splitPayment}
          />
          <PublicModalWrapper
            show={priceUpdateModalState}
            handleClose={() => setPriceUpdateModalState(false)}
            wantCloseIcon={false}
            clx={`price-update-modal-wrapper`}
          >
            <PriceUpdateModal
              oldPrice={price.total - price.discount}
              newPrice={updatedPrice.total - updatedPrice.discount}
              priceIncreased={updatedPrice.total - price.total > 0}
              quotationId={quotationId}
              setPrice={setPrice}
              updatedPrice={updatedPrice}
              setData={setData}
              handleClose={() => setPriceUpdateModalState(false)}
            />
          </PublicModalWrapper>
          <PublicModalWrapper
            show={cabinBlockedModalState}
            handleClose={() => setCabinBlockedModalState(false)}
            wantCloseIcon={false}
            clx={`cabin-blocked-modal-wrapper`}
          >
            <CabinBlockModal />
          </PublicModalWrapper>
        </>
      )}
    </div>
  );
};

export default PublicPaymentPage;
